import { ac, roles } from "@coach/shared/access";
import { organizationClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";

const baseURL = import.meta.env.VITE_PUBLIC_API_URL ?? "http://localhost:28780";

const TOKEN_KEY = "coach.bearer";

/**
 * Read the session bearer token captured on the most recent sign-in / sign-up
 * (and replayed on every API call by `apiFetch` / the typed api client).
 *
 * Why bearer and not cookies? The Tauri webview runs on `tauri://localhost`
 * / `http://localhost:28782`, the API on `http://localhost:28780`. Browsers
 * only forward cookies cross-site when `SameSite=None; Secure` is set —
 * which doesn't survive plain-http localhost in every webview. Bearer is
 * the path Better Auth's docs recommend for native apps; the server enables
 * it via the `bearer()` plugin.
 */
export function getBearerToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function clearBearerToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Better Auth client used by the Tauri/PWA app. The custom `fetch` wrapper
 * lets us:
 *   - send the previously-issued bearer token on every request (so /me, etc.
 *     work cross-origin without cookies); and
 *   - capture the `set-auth-token` response header from sign-in / sign-up
 *     and persist it for next time.
 */
export const authClient = createAuthClient({
  baseURL,
  fetchOptions: {
    auth: {
      type: "Bearer",
      token: () => getBearerToken() ?? "",
    },
    onSuccess: (ctx) => {
      const authToken = ctx.response.headers.get("set-auth-token");
      if (authToken) {
        try {
          localStorage.setItem(TOKEN_KEY, authToken);
        } catch {
          /* ignore */
        }
      }
    },
  },
  plugins: [organizationClient({ ac, roles })],
});

type AuthClient = typeof authClient;
export const signIn: AuthClient["signIn"] = authClient.signIn;
export const signUp: AuthClient["signUp"] = authClient.signUp;
export const signOut: AuthClient["signOut"] = authClient.signOut;
export const useSession: AuthClient["useSession"] = authClient.useSession;
